/**
 * The words a Visit uses to say its Job cannot be created (WBS-12662, WBS-12695, WBS-12696), shared by the
 * API that derives them and the web that shows them, so the board, the tag and the Create job reason agree.
 *
 * A readiness gap is one of two kinds: something the Job still NEEDS ("a price book (...)"), which reads
 * after "Needs" / "Jobs need", or a BLOCKER that reads alone ("Price book expired (...)"). A blocker is
 * recognised by its leading constant, so its first words must stay exactly these constants.
 */

/** The resolved price book's end date has passed; Job creation would refuse it. */
export const VISIT_JOB_PRICE_BOOK_EXPIRED = 'Price book expired';

/** The resolved price book's start date is still ahead; Job creation would refuse it. */
export const VISIT_JOB_PRICE_BOOK_NOT_ACTIVE = 'Price book not active yet';

/** Lead of the readiness line for a Visit whose automatic Job creation parked (WBS-12696). */
export const VISIT_JOB_CREATION_FAILED = 'Job creation failed';

const VISIT_JOB_READINESS_BLOCKERS: readonly string[] = [VISIT_JOB_PRICE_BOOK_EXPIRED, VISIT_JOB_PRICE_BOOK_NOT_ACTIVE];

/** True for a gap that reads on its own rather than after "needs". */
export function isVisitJobReadinessBlocker(gap: string): boolean {

    return VISIT_JOB_READINESS_BLOCKERS.some(blocker => gap.startsWith(blocker));
}

/**
 * One sentence for a list of gaps: the needs after `lead` ("Jobs need a contact, an owner"), then each
 * blocker on its own, joined by "; ". Used for tooltips and the Create job reason.
 */
export function visitJobReadinessSentence(gaps: readonly string[], lead: string): string {

    const needs = gaps.filter(gap => !isVisitJobReadinessBlocker(gap));
    const blockers = gaps.filter(isVisitJobReadinessBlocker);
    const parts = needs.length ? [`${lead} ${needs.join(', ')}`, ...blockers] : blockers;
    return parts.join('; ');
}

/** "Job creation failed: <reason>", the readiness line of a Visit whose stamp message parked. */
export function visitJobCreationFailedText(message: string): string {

    return `${VISIT_JOB_CREATION_FAILED}: ${message}`;
}
